import * as React from 'react'
import { useMutation } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { postJSON } from '@/lib/api'
import type { OrganizationProfile, ProfileMembership, RosterPlace } from '@/lib/contacts/types'

export function RosterDialog({
  open,
  onOpenChange,
  organization,
  membership,
  onAdded,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  organization: OrganizationProfile
  membership: ProfileMembership
  onAdded: (place: RosterPlace) => void
}) {
  const [personId, setPersonId] = React.useState('')
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [tab, setTab] = React.useState('existing')
  const add = useMutation({
    mutationFn: () =>
      postJSON<{ roster_place: RosterPlace }>(
        `/api/v1/organizations/${organization.id}/memberships/${membership.id}/roster_places`,
        { roster_place: tab === 'existing' ? { person_id: Number(personId) } : { name, email } },
      ),
    onSuccess: (data) => {
      onAdded(data.roster_place)
      onOpenChange(false)
    },
  })
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add to roster</DialogTitle>
          <DialogDescription>
            {organization.name} · {membership.roster_count ?? 0} places used
          </DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="existing">Existing contact</TabsTrigger>
            <TabsTrigger value="new">New person</TabsTrigger>
          </TabsList>
          <TabsContent value="existing">
            <Label htmlFor="roster-person-id">Person</Label>
            <Select value={personId} onValueChange={setPersonId}>
              <SelectTrigger id="roster-person-id">
                <SelectValue placeholder="Select a person" />
              </SelectTrigger>
              <SelectContent>
                {organization.people.map((person) => (
                  <SelectItem key={person.person_id} value={String(person.person_id)}>
                    {person.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </TabsContent>
          <TabsContent value="new">
            <div className="space-y-3">
              <div>
                <Label htmlFor="roster-new-name">Name</Label>
                <Input
                  id="roster-new-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Full name"
                />
              </div>
              <div>
                <Label htmlFor="roster-new-email">Email</Label>
                <Input
                  id="roster-new-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="name@example.com"
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => add.mutate()} disabled={add.isPending}>
            Add to roster
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
